/**
 * AI 助教切换器 + 助教偏好 — teacher-role spec §5.3 (M3)。
 * 单学科老师不渲染切换 UI(直接绑定);多学科渲染 chips。
 * 「助教偏好」是预填建议卡片(零 prompt 原则):语气/年级段结构化
 * 选项 + 一个可选的补充说明框(不叫 prompt,不留空白框诱导)。
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useEmployees, usePutEmployeePrefs, type EmployeeRow } from "@/api/school";

export function useMyEmployees() {
  const q = useEmployees();
  return { employees: q.data ?? [], loading: q.isLoading };
}

export function EmployeeSwitcher({
  employees,
  activeAgentId,
  onPick,
}: {
  employees: EmployeeRow[];
  activeAgentId: string;
  onPick: (agentId: string) => void;
}) {
  const { t } = useTranslation();
  const [prefsFor, setPrefsFor] = useState<EmployeeRow | null>(null);
  if (employees.length === 0) return null;

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {employees.length > 1 &&
        employees.map((e) => (
          <button
            key={e.subject_id}
            onClick={() => onPick(e.agent_id)}
            className={`px-2.5 py-1 rounded-full text-xs border ${
              activeAgentId === e.agent_id
                ? "bg-sky-600 text-white border-sky-600"
                : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
            }`}
            title={e.registered ? "" : t("employees.notRegistered")}
          >
            {e.name}
            {!e.registered && " ⏳"}
          </button>
        ))}
      {(() => {
        const active = employees.find((e) => e.agent_id === activeAgentId) ?? employees[0];
        return (
          <button
            onClick={() => active && setPrefsFor(active)}
            className="px-2 py-1 rounded-full text-xs border border-slate-200 text-slate-400 hover:bg-slate-50"
          >
            ⚙ {t("employees.prefs")}
          </button>
        );
      })()}
      {prefsFor && <PrefsDialog employee={prefsFor} onClose={() => setPrefsFor(null)} />}
    </div>
  );
}

const TONE_OPTIONS = ["concise", "detailed"] as const;

function PrefsDialog({ employee, onClose }: { employee: EmployeeRow; onClose: () => void }) {
  const { t } = useTranslation();
  const save = usePutEmployeePrefs();
  const [tone, setTone] = useState(employee.prefs?.tone ?? "");
  const [gradeBand, setGradeBand] = useState(employee.prefs?.grade_band ?? "");
  const [custom, setCustom] = useState(employee.custom_prompt ?? "");

  async function submit() {
    await save.mutateAsync({
      subject_id: employee.subject_id,
      custom_prompt: custom.trim() || null,
      prefs: {
        ...(tone ? { tone } : {}),
        ...(gradeBand.trim() ? { grade_band: gradeBand.trim() } : {}),
      },
    });
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40" onClick={onClose}>
      <div className="w-[24rem] rounded-xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold text-slate-800 mb-1">
          {employee.name} · {t("employees.prefs")}
        </h3>
        <p className="mb-3 text-xs text-slate-400">{t("employees.prefsHint")}</p>

        <div className="mb-3">
          <span className="text-xs text-slate-600">{t("employees.tone")}</span>
          <div className="mt-1 flex gap-1.5">
            {TONE_OPTIONS.map((o) => (
              <button
                key={o}
                onClick={() => setTone(tone === o ? "" : o)}
                className={`px-2.5 py-1 rounded-full text-xs border ${
                  tone === o ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-600 border-slate-300"
                }`}
              >
                {t(`employees.tones.${o}`)}
              </button>
            ))}
          </div>
        </div>

        <label className="block mb-3 text-xs text-slate-600">
          {t("employees.gradeBand")}
          <input
            value={gradeBand}
            onChange={(e) => setGradeBand(e.target.value)}
            placeholder={t("employees.gradeBandPlaceholder")}
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
          />
        </label>

        <label className="block mb-4 text-xs text-slate-600">
          {t("employees.custom")}
          <textarea
            value={custom}
            onChange={(e) => setCustom(e.target.value.slice(0, 2000))}
            rows={3}
            placeholder={t("employees.customPlaceholder")}
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
          />
          <span className="text-slate-300">{custom.length}/2000</span>
        </label>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded border border-slate-200 text-sm text-slate-600">
            {t("common.cancel")}
          </button>
          <button
            onClick={submit}
            disabled={save.isPending}
            className="px-3 py-1.5 rounded bg-sky-600 text-white text-sm disabled:opacity-50"
          >
            {t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

/** 把偏好渲染成 context_note 文本(nova-os 会提升为系统消息)。 */
export function prefsToContextNote(e: EmployeeRow | undefined): string | null {
  if (!e) return null;
  const parts: string[] = [];
  if (e.prefs?.tone === "concise") parts.push("回答风格:偏简洁,直给要点");
  if (e.prefs?.tone === "detailed") parts.push("回答风格:偏详细,带步骤与示例");
  if (e.prefs?.grade_band) parts.push(`默认年级段:${e.prefs.grade_band}`);
  if (e.custom_prompt?.trim()) parts.push(`补充要求:${e.custom_prompt.trim()}`);
  if (parts.length === 0) return null;
  return `【助教偏好】${parts.join(";")}`;
}
